import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, BarChart3, ArrowRight, MapPin, DollarSign, TrendingUp, Scale, Building } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { safeFetch } from '../../config/api';

// Helper function to generate canonical comparison slug
const getCanonicalComparisonSlug = (slugA, slugB) => {
  const slugs = [slugA, slugB].sort();
  return `${slugs[0]}-vs-${slugs[1]}`;
};

/**
 * Compare With Other Cities Section
 * Shows comparison links prioritized by: same country → similar cost → similar scores
 */
export const CompareWithCities = ({ citySlug, cityName, maxLinks = 5 }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await safeFetch(`/api/internal-links/city/${citySlug}?max_links=${maxLinks}`);
        if (response.ok) {
          const data = await response.json();
          setLinks(data.compare_with || []);
        }
      } catch (error) {
        console.error('Failed to fetch comparison links:', error);
      } finally {
        setLoading(false);
      }
    };

    if (citySlug) {
      fetchLinks();
    }
  }, [citySlug, maxLinks]);

  if (loading || links.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100" data-testid="compare-with-cities">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-slate-900">Compare {cityName} With Other Cities</h3>
      </div>
      <div className="space-y-2">
        {links.map((link) => (
          <a
            key={link.url}
            href={link.url}
            className="flex items-center justify-between p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
            data-testid={`compare-link-${link.city_slug}`}
          >
            <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">
              {link.label}
            </span>
            <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors" />
          </a>
        ))}
      </div>
    </div>
  );
};

/**
 * Similar Cities Section
 * Shows cities with similar characteristics for discovery
 */
export const SimilarCitiesSection = ({ citySlug, cityName, maxLinks = 5 }) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSimilar = async () => {
      try {
        const response = await safeFetch(`/api/internal-links/city/${citySlug}?max_links=${maxLinks}`);
        if (response.ok) {
          const data = await response.json();
          setCities(data.similar_cities || []);
        }
      } catch (error) {
        console.error('Failed to fetch similar cities:', error);
      } finally {
        setLoading(false);
      }
    };

    if (citySlug) {
      fetchSimilar();
    }
  }, [citySlug, maxLinks]);

  if (loading || cities.length === 0) return null;

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-100" data-testid="similar-cities-section">
      <div className="flex items-center gap-2 mb-4">
        <Globe className="w-5 h-5 text-blue-600" />
        <h3 className="font-semibold text-slate-900">Cities Similar to {cityName}</h3>
      </div>
      <div className="space-y-3">
        {cities.map((city) => (
          <a
            key={city.slug}
            href={city.url}
            className="flex items-center justify-between p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
            data-testid={`similar-city-${city.slug}`}
          >
            <div className="flex items-center gap-3">
              {city.image_url && (
                <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                  <img 
                    src={city.image_url} 
                    alt={city.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              )}
              <div>
                <span className="text-sm font-medium text-slate-900 group-hover:text-blue-600 transition-colors">
                  {city.name}
                </span>
                {city.country_name && (
                  <p className="text-xs text-slate-500">{city.country_name}</p>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs font-medium text-blue-600">
                {city.nomad_score?.toFixed(1)}/10
              </span>
              <p className="text-xs text-slate-400">NomadIQ</p>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

/**
 * Related Rankings Section
 * Shows links to relevant ranking pages
 */
export const RelatedRankings = ({ citySlug }) => {
  const [rankings, setRankings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRankings = async () => {
      try {
        const response = await safeFetch(`/api/internal-links/city/${citySlug}`);
        if (response.ok) {
          const data = await response.json();
          setRankings(data.related_rankings || []);
        }
      } catch (error) {
        console.error('Failed to fetch related rankings:', error);
      } finally {
        setLoading(false);
      }
    };

    if (citySlug) {
      fetchRankings();
    }
  }, [citySlug]);

  if (loading || rankings.length === 0) return null;

  return (
    <div className="bg-slate-50 rounded-xl p-6 border border-slate-200" data-testid="related-rankings">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp className="w-5 h-5 text-slate-600" />
        <h3 className="font-semibold text-slate-900">Explore Rankings</h3>
      </div>
      <div className="flex flex-wrap gap-2">
        {rankings.map((ranking) => (
          <a
            key={ranking.url}
            href={ranking.url}
            className="px-3 py-2 bg-white rounded-lg text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition-colors border border-slate-200"
          >
            {ranking.label}
          </a>
        ))}
      </div>
    </div>
  );
};

/**
 * Country Internal Links
 * For country detail pages - shows cities and country comparisons
 */
export const CountryInternalLinks = ({ countrySlug, countryName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await safeFetch(`/api/internal-links/country/${countrySlug}`);
        if (response.ok) {
          setData(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch country links:', error);
      } finally {
        setLoading(false);
      }
    };

    if (countrySlug) {
      fetchLinks();
    }
  }, [countrySlug]);

  if (loading || !data) return null;

  return (
    <div className="space-y-6">
      {/* Compare Countries */}
      {data.compare_with?.length > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100" data-testid="compare-countries">
          <div className="flex items-center gap-2 mb-4">
            <Scale className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-900">Compare {countryName}</h3>
          </div>
          <div className="space-y-2">
            {data.compare_with.map((link) => (
              <a
                key={link.url}
                href={link.url}
                className="flex items-center justify-between p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg">{link.flag_emoji}</span>
                  <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600 transition-colors">
                    {link.label}
                  </span>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600 transition-colors" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Cities in Country */}
      {data.cities?.length > 0 && (
        <div className="bg-gradient-to-br from-blue-50 to-slate-50 rounded-xl p-6 border border-blue-100" data-testid="cities-in-country">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-900">Popular Cities in {countryName}</h3>
          </div>
          <div className="space-y-3">
            {data.cities.map((city) => (
              <a
                key={city.slug}
                href={city.url}
                className="flex items-center justify-between p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
              >
                <div className="flex items-center gap-3">
                  {city.image_url && (
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={city.image_url} 
                        alt={city.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  )}
                  <span className="text-sm font-medium text-slate-900 group-hover:text-blue-600">
                    {city.name}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-medium text-blue-600">{city.nomad_score?.toFixed(1)}/10</span>
                  <p className="text-xs text-slate-400">{formatCurrency(city.monthly_cost)}/mo</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Comparison Page Internal Links
 * For city comparison pages - shows alternative comparisons, similar cities, nearby cities
 * Optimized for SEO crawl depth with 15+ internal links per section
 */
export const ComparisonInternalLinks = ({ cityASlug, cityBSlug, cityAName, cityBName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLinks = async () => {
      if (!cityASlug || !cityBSlug) return;
      
      const comparisonSlug = getCanonicalComparisonSlug(cityASlug, cityBSlug);
      try {
        const response = await safeFetch(`/api/internal-links/comparison/${comparisonSlug}?max_links=15`);
        if (response.ok) {
          setData(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch comparison links:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLinks();
  }, [cityASlug, cityBSlug]);

  if (loading || !data) return null;

  return (
    <div className="space-y-8">
      {/* City Pages - 8 links */}
      {data.city_pages?.length > 0 && (
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200" data-testid="city-pages-links">
          <h3 className="font-semibold text-slate-900 mb-4">Explore Each City</h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {data.city_pages.map((page) => (
              <a
                key={page.url}
                href={page.url}
                className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-blue-50 transition-colors group border border-slate-100"
              >
                <span className="text-sm text-slate-700 group-hover:text-blue-600 truncate pr-2">
                  {page.label}
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* More City Comparisons - 15 links */}
      {data.alternative_comparisons?.length > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100" data-testid="alternative-comparisons">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-900">More City Comparisons</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
            {data.alternative_comparisons.slice(0, 15).map((link) => (
              <a
                key={link.url}
                href={link.url}
                className="flex items-center justify-between p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
              >
                <span className="text-sm text-slate-700 group-hover:text-indigo-600 truncate pr-2">
                  {link.label}
                </span>
                <ArrowRight className="w-3 h-3 text-slate-400 group-hover:text-indigo-600 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Similar Cities - 10 links */}
      {data.similar_cities?.length > 0 && (
        <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-6 border border-blue-100" data-testid="similar-cities-section">
          <div className="flex items-center gap-2 mb-4">
            <Globe className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-slate-900">Similar Cities</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.similar_cities.slice(0, 10).map((city) => (
              <a
                key={city.slug}
                href={city.url}
                className="flex items-center justify-between p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
              >
                <span className="text-sm text-slate-700 group-hover:text-blue-600 truncate">
                  {city.name}
                </span>
                <span className="text-xs text-blue-500 ml-1">{city.nomad_score?.toFixed(1)}</span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Nearby Cities - 10 links */}
      {data.nearby_cities?.length > 0 && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border border-green-100" data-testid="nearby-cities-section">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-green-600" />
            <h3 className="font-semibold text-slate-900">Nearby Cities</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {data.nearby_cities.slice(0, 10).map((city) => (
              <a
                key={city.slug}
                href={city.url}
                className="flex items-center justify-between p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
              >
                <span className="text-sm text-slate-700 group-hover:text-green-600 truncate">
                  {city.name}
                </span>
                <ArrowRight className="w-3 h-3 text-slate-400 group-hover:text-green-600 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Country Hub Links */}
      {data.country_links?.length > 0 && (
        <div className="bg-slate-100 rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <Building className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Explore by Country</h3>
          </div>
          <div className="flex flex-wrap gap-3">
            {data.country_links.map((link) => (
              <a
                key={link.url}
                href={link.url}
                className="px-4 py-2 bg-white rounded-lg text-sm text-slate-700 hover:text-indigo-600 hover:bg-indigo-50 transition-colors border border-slate-200"
              >
                {link.label}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Related Rankings */}
      {data.related_rankings?.length > 0 && (
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">Explore Rankings & Tools</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {data.related_rankings.map((ranking) => (
              <a
                key={ranking.url}
                href={ranking.url}
                className="px-3 py-2 bg-white rounded-lg text-sm text-slate-600 hover:text-blue-600 hover:bg-blue-50 transition-colors border border-slate-200"
              >
                {ranking.label}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Ranking Page Internal Links
 * For ranking pages - shows comparisons between top ranked cities
 */
export const RankingInternalLinks = ({ rankingType }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await safeFetch(`/api/internal-links/ranking/${rankingType}`);
        if (response.ok) {
          setData(await response.json());
        }
      } catch (error) {
        console.error('Failed to fetch ranking links:', error);
      } finally {
        setLoading(false);
      }
    };

    if (rankingType) {
      fetchLinks();
    }
  }, [rankingType]);

  if (loading || !data) return null;

  return (
    <div className="space-y-6">
      {/* Top Cities */}
      {data.top_cities?.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-100" data-testid="top-cities-links">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-slate-900">Explore Top Ranked Cities</h3>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.top_cities.map((city, index) => (
              <a
                key={city.slug}
                href={city.url}
                className="flex items-center gap-2 p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
              >
                <span className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center text-xs font-bold text-amber-700">
                  {index + 1}
                </span>
                <span className="text-sm font-medium text-slate-700 group-hover:text-amber-600">
                  {city.name}
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Top Comparisons */}
      {data.top_comparisons?.length > 0 && (
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-6 border border-indigo-100" data-testid="top-comparisons">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            <h3 className="font-semibold text-slate-900">Compare Top Cities</h3>
          </div>
          <div className="space-y-2">
            {data.top_comparisons.map((link) => (
              <a
                key={link.url}
                href={link.url}
                className="flex items-center justify-between p-3 bg-white/60 rounded-lg hover:bg-white transition-colors group"
              >
                <span className="text-sm font-medium text-slate-700 group-hover:text-indigo-600">
                  {link.label}
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-indigo-600" />
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Other Rankings */}
      {data.other_rankings?.length > 0 && (
        <div className="bg-slate-50 rounded-xl p-6 border border-slate-200" data-testid="other-rankings">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-900">More Rankings</h3>
          </div>
          <div className="grid md:grid-cols-2 gap-2">
            {data.other_rankings.map((ranking) => (
              <a
                key={ranking.url}
                href={ranking.url}
                className="flex items-center justify-between p-3 bg-white rounded-lg hover:bg-blue-50 transition-colors group border border-slate-100"
              >
                <span className="text-sm text-slate-700 group-hover:text-blue-600">
                  {ranking.label}
                </span>
                <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Similar Cities for Expats Section
 * Shows 4 cities from the same region/country
 * For use at the bottom of city pages
 */
export const SimilarCitiesForExpats = ({ citySlug, cityName, allCities = [] }) => {
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSimilar = async () => {
      try {
        // If allCities provided, use that; otherwise fetch from API
        if (allCities.length > 0) {
          // Filter out current city and get same country first
          const currentCity = allCities.find(c => c.slug === citySlug);
          const otherCities = allCities.filter(c => c.slug !== citySlug);
          
          // Prioritize same country
          const sameCountry = otherCities.filter(c => 
            currentCity && c.country_name === currentCity.country_name
          );
          
          // If we have 4+ from same country, use those
          if (sameCountry.length >= 4) {
            setCities(sameCountry.slice(0, 4));
          } else {
            // Otherwise, fill with random cities
            const remaining = otherCities.filter(c => 
              !sameCountry.find(sc => sc.slug === c.slug)
            );
            const shuffled = [...remaining].sort(() => 0.5 - Math.random());
            setCities([...sameCountry, ...shuffled].slice(0, 4));
          }
          setLoading(false);
        } else {
          // Fetch from API
          const response = await safeFetch(`/api/internal-links/city/${citySlug}?max_links=4`);
          if (response.ok) {
            const data = await response.json();
            setCities(data.similar_cities || []);
          }
          setLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch similar cities:', error);
        setLoading(false);
      }
    };

    if (citySlug) {
      fetchSimilar();
    }
  }, [citySlug, allCities]);

  if (loading || cities.length === 0) return null;

  // Calculate monthly cost for display
  const getMonthlyCost = (city) => {
    return (city.rent_1br || 0) + (city.groceries_cost || 0) + 
           (city.transport_cost || 0) + (city.healthcare_cost || 0) + 
           (city.internet_cost || 0);
  };

  return (
    <section className="py-12 bg-gradient-to-br from-slate-50 to-blue-50" data-testid="similar-cities-expats">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">
          Similar Cities for Expats
        </h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {cities.map((city) => (
            <a
              key={city.slug}
              href={city.url || `/cities/${city.slug}`}
              className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all group border border-slate-100"
              data-testid={`similar-city-card-${city.slug}`}
            >
              {city.image_url && (
                <div className="h-32 overflow-hidden">
                  <img 
                    src={city.image_url} 
                    alt={city.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="p-4">
                <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors mb-1">
                  {city.name}
                </h3>
                <p className="text-sm text-slate-500 mb-2">
                  {city.country_name}
                </p>
                <p className="text-sm font-medium text-blue-600">
                  {formatCurrency(city.monthly_cost || getMonthlyCost(city))}/month
                </p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};
